import React, { useEffect, useMemo, useState } from "react";
import { Boxes, Plus, Tag, CalendarDays, Upload } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const asset0 = { partner_id: "", name: "", universe: "hospitality", category: "hotel", description: "", city: "Kinshasa", zone: "", status: "active", details: {}, images: [] };
const offer0 = { asset_id: "", name: "", transaction_type: "rent", pricing_model: "night", price: "", currency: "USD", conditions: "", status: "available", package_items: [], inventory_mode: "single", capacity: 1, unit_label: "unité", listing_id: null };
const av0 = { offer_id: "", start_at: "", end_at: "", status: "available", quantity: 1, notes: "" };
const bookingStatuses = [
  ["hold", "En attente"],
  ["confirmed", "Confirmée"],
  ["cancelled", "Annulée"],
  ["completed", "Terminée"],
  ["no_show", "Absent"],
];

export default function PartnerCatalog() {
  const [assets, setAssets] = useState([]);
  const [offers, setOffers] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("assets");
  const [asset, setAsset] = useState(asset0);
  const [offer, setOffer] = useState(offer0);
  const [av, setAv] = useState(av0);

  const load = () => Promise.all([
    api.get("/catalog/assets"),
    api.get("/catalog/offers"),
    api.get("/catalog/availability"),
    api.get("/catalog/bookings"),
  ]).then(([a, o, v, b]) => {
    setAssets(a.data || []);
    setOffers(o.data || []);
    setAvailability(v.data || []);
    setBookings(b.data || []);
  });

  useEffect(() => { load(); }, []);
  const byAsset = useMemo(() => Object.fromEntries(assets.map(x => [x.id, x])), [assets]);

  const saveAsset = async e => {
    e.preventDefault();
    await api.post("/catalog/assets", asset);
    toast.success("Actif ajouté");
    setAsset(asset0);
    load();
  };
  const saveOffer = async e => {
    e.preventDefault();
    await api.post("/catalog/offers", { ...offer, price: Number(offer.price) || 0 });
    toast.success("Offre ajoutée");
    setOffer(offer0);
    load();
  };
  const saveAv = async e => {
    e.preventDefault();
    await api.post("/catalog/availability", { ...av, quantity: Number(av.quantity) || 1 });
    toast.success("Disponibilité ajoutée");
    setAv(av0);
    load();
  };
  const publish = async id => {
    await api.post("/catalog/offers/" + id + "/listing");
    toast.success("Annonce créée dans l’inventaire");
    load();
  };
  const sync = async id => {
    await api.post("/catalog/offers/" + id + "/sync");
    toast.success("Annonce synchronisée");
    load();
  };
  const changeBookingStatus = async (id, status) => {
    await api.patch("/catalog/bookings/" + id + "/status", { status });
    toast.success("Statut de réservation mis à jour");
    load();
  };

  return <div className="space-y-6">
    <div>
      <div className="text-xs font-bold uppercase tracking-[.25em] text-brand-orange">Catalogue partenaires</div>
      <h1 className="mt-2 font-display text-4xl text-brand-navy">Actifs, offres & disponibilités</h1>
      <p className="mt-2 text-sm text-slate-500">Un actif peut proposer plusieurs prix, packs et périodes sans être dupliqué.</p>
    </div>

    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {[[Boxes, "Actifs", assets.length], [Tag, "Offres", offers.length], [CalendarDays, "Réservations", bookings.length]].map(([Icon, label, value]) =>
        <div className="rounded-2xl border bg-white p-4" key={label}>
          <Icon className="h-5 w-5 text-brand-orange" />
          <b className="mt-2 block text-2xl">{value}</b>
          <span className="text-xs text-slate-500">{label}</span>
        </div>
      )}
    </div>

    <div className="flex flex-wrap gap-2">
      {["assets", "offers", "availability", "bookings"].map(x =>
        <Button key={x} onClick={() => setTab(x)} variant={tab === x ? "default" : "outline"} className={tab === x ? "bg-brand-navy text-white" : ""}>
          {x === "assets" ? "Actifs" : x === "offers" ? "Offres" : x === "availability" ? "Disponibilités" : "Réservations"}
        </Button>
      )}
    </div>

    {tab === "assets" && <>
      <Form onSubmit={saveAsset}>
        <F l="Partenaire / apporteur ID" v={asset.partner_id} c={v => setAsset({ ...asset, partner_id: v })} />
        <F l="Nom de l’actif *" v={asset.name} c={v => setAsset({ ...asset, name: v })} />
        <Sel l="Univers" v={asset.universe} c={v => setAsset({ ...asset, universe: v })} o={["hospitality", "auto", "real_estate", "spaces", "business"]} />
        <F l="Catégorie" v={asset.category} c={v => setAsset({ ...asset, category: v })} />
        <F l="Ville" v={asset.city} c={v => setAsset({ ...asset, city: v })} />
        <F l="Zone" v={asset.zone} c={v => setAsset({ ...asset, zone: v })} />
      </Form>
      <List rows={assets} render={x => <><b>{x.name}</b><span>{x.universe} · {x.category} · {x.zone || x.city}</span></>} />
    </>}

    {tab === "offers" && <>
      <Form onSubmit={saveOffer}>
        <Sel l="Actif *" v={offer.asset_id} c={v => setOffer({ ...offer, asset_id: v })} o={assets.map(x => x.id)} labels={byAsset} />
        <F l="Nom de l’offre *" v={offer.name} c={v => setOffer({ ...offer, name: v })} />
        <Sel l="Transaction" v={offer.transaction_type} c={v => setOffer({ ...offer, transaction_type: v })} o={["rent", "sale"]} />
        <Sel l="Tarification" v={offer.pricing_model} c={v => setOffer({ ...offer, pricing_model: v })} o={["hour", "night", "day", "week", "month", "event", "fixed", "quote"]} />
        <F l="Prix" t="number" v={offer.price} c={v => setOffer({ ...offer, price: v })} />
        <Sel l="Gestion du stock" v={offer.inventory_mode} c={v => setOffer({ ...offer, inventory_mode: v, capacity: v === "single" ? 1 : offer.capacity })} o={["single", "capacity"]} labels={{ single: { name: "Bien unique (villa, voiture…)" }, capacity: { name: "Plusieurs unités (chambres, flotte…)" } }} />
        {offer.inventory_mode === "capacity" && <F l="Capacité totale *" t="number" v={offer.capacity} c={v => setOffer({ ...offer, capacity: Number(v) })} />}
        <F l="Nom de l’unité" v={offer.unit_label} c={v => setOffer({ ...offer, unit_label: v })} />
        <F l="Conditions" v={offer.conditions} c={v => setOffer({ ...offer, conditions: v })} />
      </Form>
      <List rows={offers} render={x => <>
        <b>{byAsset[x.asset_id]?.name || "Actif"} — {x.name}</b>
        <span>{Number(x.price).toLocaleString()} {x.currency} / {x.pricing_model}</span>
        <span>{x.inventory_mode === "capacity" ? `${x.capacity || 1} ${x.unit_label || "unité"}(s)` : "Bien unique"}</span>
        {x.listing_id
          ? <Button size="sm" variant="outline" onClick={() => sync(x.id)}><Upload className="mr-1 h-3 w-3" />Synchroniser l’annonce</Button>
          : <Button size="sm" onClick={() => publish(x.id)}><Upload className="mr-1 h-3 w-3" />Créer l’annonce</Button>}
      </>} />
    </>}

    {tab === "availability" && <>
      <Form onSubmit={saveAv}>
        <Sel l="Offre *" v={av.offer_id} c={v => setAv({ ...av, offer_id: v })} o={offers.map(x => x.id)} labels={Object.fromEntries(offers.map(x => [x.id, { name: x.name }]))} />
        <F l="Début" t="datetime-local" v={av.start_at} c={v => setAv({ ...av, start_at: v })} />
        <F l="Fin" t="datetime-local" v={av.end_at} c={v => setAv({ ...av, end_at: v })} />
        <F l="Quantité disponible" t="number" v={av.quantity} c={v => setAv({ ...av, quantity: v })} />
      </Form>
      <List rows={availability} render={x => <>
        <b>{offers.find(o => o.id === x.offer_id)?.name || "Offre"}</b>
        <span>{new Date(x.start_at).toLocaleString("fr-FR")} → {new Date(x.end_at).toLocaleString("fr-FR")} · {x.quantity} disponible(s)</span>
      </>} />
    </>}

    {tab === "bookings" && <List rows={bookings} render={x => <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <b>{x.reference || "Réservation"}</b>
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">{bookingStatuses.find(s => s[0] === x.status)?.[1] || x.status}</span>
      </div>
      <span>{offers.find(o => o.id === x.offer_id)?.name || "Offre"} · {x.quantity || 1} unité(s)</span>
      <span>{new Date(x.start_at).toLocaleString("fr-FR")} → {new Date(x.end_at).toLocaleString("fr-FR")}</span>
      <span>{x.customer_name || "Client"} · {x.customer_phone || x.customer_email || "Coordonnées non précisées"}</span>
      <label className="mt-2">
        <Label>Changer le statut</Label>
        <select className="mt-1 h-10 w-full rounded-md border px-3" value={x.status} onChange={e => changeBookingStatus(x.id, e.target.value)}>
          {bookingStatuses.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select>
      </label>
    </>} />}
  </div>;
}

function Form({ children, onSubmit }) {
  return <form onSubmit={onSubmit} className="grid gap-4 rounded-2xl border bg-white p-5 sm:grid-cols-2 lg:grid-cols-3">
    {children}
    <div className="flex items-end"><Button className="bg-brand-orange text-white"><Plus className="mr-1 h-4 w-4" />Ajouter</Button></div>
  </form>;
}
function F({ l, v, c, t = "text" }) {
  return <label><Label>{l}</Label><Input className="mt-1" type={t} value={v ?? ""} onChange={e => c(e.target.value)} required={l.includes("*")} /></label>;
}
function Sel({ l, v, c, o, labels = {} }) {
  return <label><Label>{l}</Label><select className="mt-1 h-10 w-full rounded-md border px-3" value={v} onChange={e => c(e.target.value)} required={l.includes("*")}>
    <option value="">Sélectionner</option>
    {o.map(x => <option value={x} key={x}>{labels[x]?.name || x}</option>)}
  </select></label>;
}
function List({ rows, render }) {
  if (!rows.length) return <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500">Aucune donnée pour le moment.</div>;
  return <div className="grid gap-3 lg:grid-cols-2">{rows.map(x => <article key={x.id} className="flex flex-col gap-1 rounded-xl border bg-white p-4">{render(x)}</article>)}</div>;
}
